import { RuleSet, run_cases } from 'shared/text-filter';

export function TextFilterViewModel(data) {

    const self = this;
    self.type = "TextFilterViewModel";

    self.rootvm = data.rootvm;

    // The rule set as the server sends it: {whitelist: [...], rules: [...]}
    self.ruleset = ko.observable(null);

    // The test cases of the real rules (text_filter/rule_cases.json)
    self.rule_cases = ko.observable([]);

    self.error_message = ko.observable("");

    self.initialized = ko.observable(false);
    self.initialize = () => {
        self.initialized(true);
        self.lookup_ruleset();
        self.lookup_artist_profiles();
    };

    // --- Rule list ---
    // One row per rule. Keyword rules get their own input for a new keyword,
    // which has to survive a reload of the rule set.
    const new_keywords = {};

    self.rules = ko.pureComputed(() => (self.ruleset()?.rules ?? []).map(rule => {
        new_keywords[rule.name] = new_keywords[rule.name] ?? ko.observable("");

        return {
            ...rule,
            is_keywords: rule.type == 'keywords',
            new_keyword: new_keywords[rule.name]
        };
    }));

    self.regex_rules = ko.pureComputed(() => self.rules().filter(rule => !rule.is_keywords));
    self.keyword_rules = ko.pureComputed(() => self.rules().filter(rule => rule.is_keywords));
    self.whitelist = ko.pureComputed(() => self.ruleset()?.whitelist ?? []);

    // Messages of the rule cases for one rule, one per line. Whitelist
    // entries have no cases of their own, they get the whitelist cases.
    self.case_messages = (rule_name, is_whitelist = false) => [...new Set(self.rule_cases()
        .filter(c => is_whitelist
            ? c.name.startsWith('whitelist')
            : c.rules === rule_name || (Array.isArray(c.rules) && c.rules.includes(rule_name)))
        .map(c => c.message))]
        .join('\n');

    // regex101 shows a JS pattern between / delimiters, so a / in the pattern
    // has to be \/ there. One that is escaped already stays as it is. For the
    // engines \/ and / mean the same, so this only changes the link.
    const escape_slashes = (pattern) => {
        let out = '';

        for (let i = 0; i < pattern.length; i++) {
            if (pattern[i] == '\\') {
                out += pattern.slice(i, i + 2);
                i += 1;
            } else {
                out += pattern[i] == '/' ? '\\/' : pattern[i];
            }
        }

        return out;
    };

    // regex101 with the pattern, the flags of our engines and some test text.
    // The JS flavor treats \w and \b like both our engines do.
    self.regex101_url = (pattern, test_text) => 'https://regex101.com/'
        + '?regex=' + encodeURIComponent(escape_slashes(pattern))
        + '&flags=gi&flavor=javascript'
        + (test_text ? '&testString=' + encodeURIComponent(test_text) : '');

    self.boundary_label = (boundary) => ({
        none: "Matches anywhere, also inside a longer word",
        start: "Matches at the start of a word",
        word: "Matches whole words only"
    }[boundary] ?? boundary);

    self.lookup_ruleset = async () => {
        self.error_message("");
        self.rootvm.is_busy(true);

        try {
            const response = await fetch(
                self.rootvm.web_host + '/api/admin/tools/text-filter');

            const payload = await response.json();

            if (response.ok && payload.success) {
                self.ruleset(payload.ruleset);
                self.rule_cases(payload.rule_cases ?? []);
            } else {
                const message = payload.message || "Could not load the rules";
                self.error_message(message);
                notyf.error(message);
            }
        } catch (error) {
            self.error_message("Could not load the rules");
            notyf.error("Could not load the rules");
            console.error(error);
        } finally {
            self.rootvm.is_busy(false);
        }
    };

    self.add_keyword = async (rule) => {
        const keyword = (rule.new_keyword() ?? "").trim();

        if (keyword.length == 0) {
            notyf.error("Enter a keyword first");
            return;
        }

        self.rootvm.is_busy(true);

        try {
            const response = await fetch(
                self.rootvm.web_host + '/api/admin/tools/text-filter/keywords', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                body: JSON.stringify({ rule_name: rule.name, keyword: keyword })
            });

            const payload = await response.json();

            if (response.ok && payload.success) {
                rule.new_keyword("");
                self.ruleset(payload.ruleset);

                if (payload.added) {
                    notyf.success(payload.message);
                } else {
                    notyf.error(payload.message);
                }
            } else {
                notyf.error(payload.message || "Could not add the keyword");
            }
        } catch (error) {
            notyf.error("Could not add the keyword");
            console.error(error);
        } finally {
            self.rootvm.is_busy(false);
        }
    };

    // --- Try it out ---
    // Runs the same JS engine the app uses on the send button.
    self.test_message = ko.observable("");
    self.test_rules = ko.observable("all");

    self.test_rule_options = ko.pureComputed(() => [
        { value: "all", label: "All rules" },
        { value: "generic", label: "Set: generic" },
        { value: "sc", label: "Set: sc" },
        ...self.rules()
            .filter(rule => rule.active)
            .map(rule => ({ value: rule.name, label: "Rule: " + rule.name }))
    ]);

    const compiled = ko.pureComputed(() => self.ruleset() ? new RuleSet(self.ruleset()) : null);

    self.test_result = ko.pureComputed(() => {
        const message = self.test_message();

        if (!compiled() || !message) {
            return null;
        }

        return compiled().filter_text(message, { rules: self.test_rules(), replace_value: null });
    });

    const escape_html = (text) => text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');

    // The message with each hit in a <mark>. We build it from the match
    // positions and not from the engine's text, so what was typed gets
    // escaped.
    self.test_marked_html = ko.pureComputed(() => {
        const message = self.test_message();
        const result = self.test_result();

        if (!result) {
            return "";
        }

        let pieces = [];
        let position = 0;

        for (const match of result.matches) {
            pieces.push(escape_html(message.slice(position, match.start)));
            pieces.push(`<mark title="${escape_html(match.rules.join(', '))}">${escape_html(match.text)}</mark>`);
            position = match.end;
        }

        pieces.push(escape_html(message.slice(position)));

        return pieces.join('');
    });

    // --- Check rules ---
    // The server runs the rule cases against the live rules with the Python
    // engine, we run them again here with the JS engine.
    self.check_rows = ko.observableArray([]);
    self.check_only_problems = ko.observable(true);

    const same_result = (a, b) => a.text === b.text
        && JSON.stringify(a.hits) == JSON.stringify(b.hits);

    self.check_summary = ko.pureComputed(() => {
        const rows = self.check_rows();

        if (rows.length == 0) {
            return null;
        }

        return {
            total: rows.length,
            python_passed: rows.filter(row => row.python.passed).length,
            js_passed: rows.filter(row => row.js.passed).length,
            engines_differ: rows.filter(row => row.engines_differ).length
        };
    });

    self.check_visible_rows = ko.pureComputed(() => self.check_rows()
        .filter(row => !self.check_only_problems() || row.problem));

    self.run_check = async () => {
        self.rootvm.is_busy(true);

        try {
            const response = await fetch(
                self.rootvm.web_host + '/api/admin/tools/text-filter/check');

            const payload = await response.json();

            if (!(response.ok && payload.success)) {
                notyf.error(payload.message || "Could not run the check");
                return;
            }

            const js_results = run_cases(payload.ruleset, payload.cases);

            self.check_rows(payload.cases.map((c, i) => {
                const python = payload.python[i];
                const js = js_results[i];
                const engines_differ = !same_result(python, js);

                return {
                    ...c,
                    rules_label: Array.isArray(c.rules) ? c.rules.join(', ') : c.rules,
                    python: python,
                    js: js,
                    engines_differ: engines_differ,
                    problem: !python.passed || !js.passed || engines_differ
                };
            }));
        } catch (error) {
            notyf.error("Could not run the check");
            console.error(error);
        } finally {
            self.rootvm.is_busy(false);
        }
    };

    // --- Artist profiles ---
    // Artists with contact details in their profile texts. The script
    // check_artist_profiles_filter.py finds them, we only show the result.
    self.profile_artists = ko.observableArray([]);
    self.profile_stats = ko.observable(null);
    self.profile_rules = ko.observableArray([]);
    self.profile_field = ko.observable('all');

    // Part of an artist name. The artist list sets it when someone follows
    // the profile indicator of one artist.
    self.profile_search = ko.observable('');

    self.profile_field_options = [
        { value: 'all', label: 'All fields' },
        { value: 'description', label: 'Description' },
        { value: 'notes', label: 'Notes' },
        { value: 'requires', label: 'Requires' },
        { value: 'slogan', label: 'Slogan' }
    ];

    // One row per artist and field.
    self.profile_rows = ko.pureComputed(() => self.profile_artists()
        .filter(artist => (artist.artist_name ?? '').toLowerCase()
            .includes(self.profile_search().trim().toLowerCase()))
        .flatMap(artist => Object.entries(artist.hits)
            .filter(([field]) => self.profile_field() == 'all' || field == self.profile_field())
            .map(([field, hit]) => ({
                artist_name: artist.artist_name,
                url: self.rootvm.frontend_web_host + '/artists/' + artist.artist_uuid,
                field: field,
                rules: hit.rules,
                snippets: hit.snippets
            }))));

    // The hit in a <mark>, with the text around it. Everything gets escaped,
    // it is text the artist typed.
    self.snippet_html = (snippet) => '…' + escape_html(snippet.before)
        + `<mark title="${escape_html(snippet.rules.join(', '))}">${escape_html(snippet.text)}</mark>`
        + escape_html(snippet.after) + '…';

    self.format_timestamp = (value) => {
        const date = new Date(value);
        return isNaN(date) ? (value ?? '-') : date.toLocaleString('de-DE');
    };

    self.lookup_artist_profiles = async () => {
        try {
            const response = await fetch(
                self.rootvm.web_host + '/api/admin/tools/text-filter/artist-profiles');

            const payload = await response.json();

            if (response.ok && payload.success) {
                self.profile_artists(payload.artists);
                self.profile_stats(payload.stats);
                self.profile_rules(payload.rules);
            } else {
                notyf.error(payload.message || "Could not load the artist profiles");
            }
        } catch (error) {
            notyf.error("Could not load the artist profiles");
            console.error(error);
        }
    };
}
